import { IconButton, Stack, styled, Tooltip } from '@mui/material'
import { ChatCenteredIcon, ChatCenteredSlashIcon } from '@phosphor-icons/react'
import { DisplayUserLink, OrderStatus, tokens } from 'ComponentLibrary'
import { CSSProperties } from 'react'
import { Typography } from 'ComponentLibrary/src/piazza/text'
import { type User } from 'ComponentLibrary/src/common/users'
import { isEmpty, uniqBy } from 'lodash'
import { UUID } from 'ComponentLibrary/src/common/uuid'

export type EmailTooltipOrder = { status: OrderStatus; observers: UUID[] }

export type EmailTooltipProps = {
    order: EmailTooltipOrder
    users: User[]
    emailText: string
    additionalNotifiedUserIds?: UUID[]
    style?: CSSProperties
}

export function EmailTooltip({ emailText, style, order, users, additionalNotifiedUserIds = [] }: EmailTooltipProps) {
    const notifiedUsers = users
        .filter((user) => order.observers.includes(user.uuid) || additionalNotifiedUserIds?.includes(user.uuid))
        .filter((user) => !user.isCurrentUser)
    const uniqueNotifiedUsers = uniqBy(notifiedUsers, (user) => user.uuid)

    const isHidden = order.status === 'temp_hide_in_portal'
    const isSendingEmail = !isHidden && !isEmpty(notifiedUsers)

    const statusText = isSendingEmail
        ? emailText
        : isHidden
          ? 'Es wird keine automatische E-Mail an Beobachter versendet, da der Auftrag versteckt ist.'
          : 'Es wird keine automatische E-Mail an Beobachter versendet, da der Auftrag keine Beobachter hat.'

    const notifiedUserListTitle = !isEmpty(additionalNotifiedUserIds) ? 'Benachrichtigte' : 'Beobachter'

    const tooltipContent = (
        <Stack direction="column" gap={tokens.SizingSpacing150} padding={tokens.SizingSpacing150} sx={{ cursor: 'default' }}>
            <Typography variant="textS" color={tokens.ColorTextDefault}>
                {statusText}
            </Typography>
            {isSendingEmail && (
                <Stack direction="column" gap={tokens.SizingSpacing075}>
                    <Typography variant="headline5" color={tokens.ColorTextDefault}>
                        {notifiedUserListTitle}
                    </Typography>
                    {uniqueNotifiedUsers.map((user) => (
                        <DisplayUserLink key={user.uuid} user={user} />
                    ))}
                </Stack>
            )}
        </Stack>
    )

    return (
        <Tooltip
            title={tooltipContent}
            placement="top"
            onClick={(event) => event.stopPropagation()}
            style={{ alignSelf: 'center', ...style }}
            componentsProps={{
                popper: {
                    sx: { zIndex: 999 },
                },
                tooltip: {
                    sx: {
                        ...tokens.Popup,
                        backgroundColor: tokens.ColorBackgroundLight,
                        padding: 0,
                        maxWidth: '420px',
                    },
                },
            }}
        >
            <StyledIconButton>
                {isSendingEmail ? (
                    <ChatCenteredIcon size={tokens.SizingIconS} weight="bold" color={tokens.ColorIconSubtle} />
                ) : (
                    <ChatCenteredSlashIcon size={tokens.SizingIconS} weight="bold" />
                )}
            </StyledIconButton>
        </Tooltip>
    )
}

const StyledIconButton = styled(IconButton)(() => ({
    color: tokens.ColorIconSubtlest,
    padding: tokens.SizingSpacing100,
    borderRadius: '50%',
    transition: 'background-color 0.2s ease-in-out',
    cursor: 'default',
    '&:hover': {
        color: tokens.ColorIconSubtlest,
        backgroundColor: tokens.ColorSurfaceHover,
    },
}))
